// T045: Game State Machine

import type { GameState, TurnPhase } from '../../types';

export type PhaseTransition =
  | 'DRAW_COMPLETE'
  | 'DEFENSE_COMPLETE'
  | 'SUMMON_COMPLETE'
  | 'TACTICS_COMPLETE'
  | 'END_COMPLETE';

export type ActionType =
  | 'draw'
  | 'defend'
  | 'summon'
  | 'summonTraveler'
  | 'attack'
  | 'move'
  | 'tacticalSummon'
  | 'endPhase';

// Phase transition map
const PHASE_TRANSITIONS: Record<TurnPhase, { event: PhaseTransition; next: TurnPhase }> = {
  draw: { event: 'DRAW_COMPLETE', next: 'defense' },
  defense: { event: 'DEFENSE_COMPLETE', next: 'summon' },
  summon: { event: 'SUMMON_COMPLETE', next: 'tactics' },
  tactics: { event: 'TACTICS_COMPLETE', next: 'end' },
  end: { event: 'END_COMPLETE', next: 'draw' },
};

// Available actions per phase
const PHASE_ACTIONS: Record<TurnPhase, ActionType[]> = {
  draw: ['draw', 'endPhase'],
  defense: ['defend', 'endPhase'],
  summon: ['summon', 'summonTraveler', 'endPhase'],
  tactics: ['attack', 'move', 'tacticalSummon', 'endPhase'],
  end: ['endPhase'],
};

/**
 * Get the next player ID.
 */
function getNextPlayerId(state: GameState): string {
  return state.activePlayerId === state.player1.id ? state.player2.id : state.player1.id;
}

/**
 * Handle end-of-turn state updates.
 */
function handleEndOfTurn(state: GameState): GameState {
  const newState = { ...state };

  // Increment turn number
  newState.turnNumber = state.turnNumber + 1;

  // Switch active player
  newState.activePlayerId = getNextPlayerId(state);

  // Handle countdown
  if (state.countdownTurns !== null) {
    const newCountdown = state.countdownTurns - 1;
    newState.countdownTurns = newCountdown;

    // Check if game ends due to countdown
    if (newCountdown <= 0) {
      newState.status = 'ended';
      newState.gameEnded = true;
      // Winner determination would happen elsewhere based on scores
    }
  }

  // NOTE: Attack declarations are NOT cleared here.
  // They persist so the next player's defense phase can resolve combat.
  // They are cleared after combat resolution in DefensePhaseHandler.

  return newState;
}

/**
 * Transition to the next phase.
 */
export function transition(state: GameState, event: PhaseTransition): GameState {
  const currentPhase = state.currentPhase;
  const expectedTransition = PHASE_TRANSITIONS[currentPhase];

  if (expectedTransition.event !== event) {
    throw new Error(
      `Invalid transition: cannot apply ${event} in ${currentPhase} phase. ` +
        `Expected ${expectedTransition.event}`
    );
  }

  let newState: GameState = {
    ...state,
    currentPhase: expectedTransition.next,
  };

  // Reset tactics action flag when entering tactics phase
  if (expectedTransition.next === 'tactics') {
    newState.tacticsActionUsed = false;
  }

  // Reset mecha merge phase state when entering summon phase
  if (expectedTransition.next === 'summon') {
    newState.mechaMergePhaseActive = false;
    newState.mechaMergeUsedThisTurn = false;
    newState.mechaMergeUsedNames = undefined;
  }

  // Handle special end-of-turn logic
  if (currentPhase === 'end') {
    newState = handleEndOfTurn(newState);
  }

  return newState;
}

/**
 * Check if an action can be performed in the current phase.
 */
export function canPerformAction(state: GameState, action: ActionType): boolean {
  const allowedActions = PHASE_ACTIONS[state.currentPhase];
  return allowedActions.includes(action);
}

/**
 * Get available actions for the current phase.
 */
export function getAvailableActions(state: GameState): ActionType[] {
  return PHASE_ACTIONS[state.currentPhase];
}

/**
 * Get the expected transition event for the current phase.
 */
export function getExpectedTransition(state: GameState): PhaseTransition {
  return PHASE_TRANSITIONS[state.currentPhase].event;
}

/**
 * Get the next phase after transition.
 */
export function getNextPhase(state: GameState): TurnPhase {
  return PHASE_TRANSITIONS[state.currentPhase].next;
}

/**
 * Check if the game has ended.
 */
export function isGameEnded(state: GameState): boolean {
  return state.status === 'ended' || state.gameEnded;
}

/**
 * Validate if a phase transition is valid.
 */
export function isValidTransition(state: GameState, event: PhaseTransition): boolean {
  const expectedTransition = PHASE_TRANSITIONS[state.currentPhase];
  return expectedTransition.event === event;
}

// Re-export as object for backwards compatibility
export const GameStateMachine = {
  transition,
  canPerformAction,
  getAvailableActions,
  getExpectedTransition,
  getNextPhase,
  isGameEnded,
  isValidTransition,
};
